import { Dispatch, FC, SetStateAction, useEffect, useMemo } from 'react';
import { HeaderProps, useRowSelect, useSortBy, useTable } from 'react-table';
import styled, { CSSProperties } from 'styled-components';
import { SortDirection } from '../../../enums/SortDirection';
import { useCheckDevice } from '../../../hooks/UseCheckDevice';
import { SortType } from '../../../types/SortType';
import { TableBody } from '../../style/TableBody';
import { TableHeader } from '../../style/TableHeader';
import { IndeterminateCheckBox } from '../indeterminate-checkbox/IndeterminateCheckBox';
import SortingIndicator from './SortingIndicator';

export interface ReactTableWrapperProps {
  gridColumns: any;
  data: any;
  canSelectRows?: boolean;
  toggleAllRowsValue?: boolean;
  refreshCount?: number;
  sort?: SortType;
  currentSortedColumn?: string;
  sortingClassName?: string;
  setCurrentSortedColumn?: Dispatch<SetStateAction<string>>;
  sortByCallBack?: (sort: SortType) => void;
  setSelectedRows?: Dispatch<SetStateAction<{}>>;
  setIsAllRowsSelected?: Dispatch<SetStateAction<boolean>>;
  onRowClick?: (row: any) => void;
  noResultFoundBody?: JSX.Element;
  visibleSortIndicator?: boolean;
  updateCellData?: any;
  tableClassName?: string;
  type?: string;
  isDynamicColumns?: boolean;
}

const selectionHook = (hooks: any) => {
  hooks.allColumns.push((columns) => [
    // Let's make a column for selection
    {
      id: '_selector',
      disableResizing: true,
      disableGroupBy: true,
      minWidth: 45,
      width: 1,
      maxWidth: 45,
      Aggregated: undefined,
      // The header can use the table's getToggleAllRowsSelectedProps method
      // to render a checkbox
      Header: ({ getToggleAllRowsSelectedProps, toggleAllRowsSelected }: HeaderProps<any>) => {
        const { onChange, ...propsWithoutOnChange } = getToggleAllRowsSelectedProps({
          title: undefined,
        });

        const overridenOnChange = (event: React.ChangeEvent<Element>) => {
          if (propsWithoutOnChange.indeterminate || propsWithoutOnChange.checked) {
            toggleAllRowsSelected(false);
          } else {
            toggleAllRowsSelected(true);
          }
          // Implement new onChange behaviour for your "header" checkbox here!
        };

        const newProps = { onChange: overridenOnChange };
        return (
          <IndeterminateCheckBox {...{ ...newProps, ...propsWithoutOnChange }} extendTouchArea />
        );
      },
      // The cell can use the individual row's getToggleRowSelectedProps method
      // to the render a checkbox
      Cell: ({ row }) => {
        return <IndeterminateCheckBox {...row.getToggleRowSelectedProps()} extendTouchArea />;
      },
    },
    ...columns,
  ]);
};

const ReactTableWrapper: FC<ReactTableWrapperProps> = (props) => {
  const defaultColumn = useMemo(
    () => ({
      minWidth: 150,
      width: 150,
      maxWidth: 500,
    }),
    [],
  );
  const updateCellData = props.updateCellData;
  const hooks = props.canSelectRows ? [selectionHook] : [];
  let columns = [];
  if (props.isDynamicColumns) {
    columns = useMemo(() => props.gridColumns, [props.gridColumns]);
  } else {
    columns = useMemo(() => props.gridColumns, []);
  }
  const data = useMemo(() => props.data, [props.data]);
  const type = props.type;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
    toggleAllRowsSelected,
    isAllRowsSelected,
    state: { selectedRowIds },
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      manualSortBy: true,
      disableSortRemove: true,
      disableMultiSort: true,
      autoResetSortBy: false,
      updateCellData,
    },
    useSortBy,
    useRowSelect,
    ...hooks,
  );

  const { isMobileDevice } = useCheckDevice();

  /** ---------------------Effects used for checkbox related toggling---------------------------------------- */

  /** Effect to send the row data outside of the component to be accessed by parent/sibling component */
  useEffect(() => {
    props?.setSelectedRows && props?.setSelectedRows(selectedFlatRows.map((x) => x.original));
  }, [props?.setSelectedRows, selectedRowIds]);

  /** Effect to toggle the checkbox values for the selection of the rows */
  useEffect(() => {
    if (!isAllRowsSelected && props.toggleAllRowsValue) {
      toggleAllRowsSelected(true);
    } else if (!props.toggleAllRowsValue) {
      toggleAllRowsSelected(false);
    }
  }, [props?.toggleAllRowsValue, props.refreshCount]);

  /** Effect to send back the flag whether all rows have been selected or not */
  useEffect(() => {
    props?.setIsAllRowsSelected && props?.setIsAllRowsSelected(isAllRowsSelected);
  }, [isAllRowsSelected, props?.setIsAllRowsSelected]);

  /**------------------------------------------------------------------------------------------------------- */
  const onSortingIconClick = (column: any): void => {
    if (column.id !== '_selector' && column.sortAccessor !== undefined) {
      const updatedSort: SortType = {
        sortDirection: props.sort.sortDirection,
        accessor: column.sortAccessor,
        subSort: column.subSort,
        subSortDirection: column.subSortDirection,
      };
      props.sortByCallBack(updatedSort);
    }
  };

  const renderSortingIcon = (e: any, column: any): void => {
    if (
      column.sortAccessor &&
      column.isSortable &&
      (!e.target.matches('.exclude-sorting *') || e.target.matches('.header-sort'))
    ) {
      props.setCurrentSortedColumn(column.sortAccessor);
      onSortingIconClick(column);
    }
  };

  const cellClassName = (cell: any): string => {
    let className = `${cell.column.className ?? ''}`;
    if (cell.column.sortAccessor === props.currentSortedColumn) {
      className = `${className} ${props.sortingClassName}`;
    }

    return className;
  };

  return (
    <table
      {...getTableProps()}
      className={`react-table-wrapper ${props.canSelectRows ? 'enabled-checkbox' : ''}`}
    >
      <TableHeader>
        {headerGroups.map((headerGroup) => (
          <tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column, index) => {
              const style = {
                width: column.width ? column.width + 'px' : '150px',
                minWidth: !isMobileDevice
                  ? column.minWidth
                    ? column.minWidth + 'px'
                    : '150px'
                  : column?.id == 'appointmentandRoStatus'
                    ? '100px'
                    : '',
                maxWidth: column.maxWidth ? column.maxWidth + 'px' : '500px',
                cursor: column.isSortable ? 'pointer' : 'auto',
                paddingRight: column.paddingRight ? column.paddingRight + 'px' : '',
                padding: column.padding ? column.padding + 'px' : '',
              } as CSSProperties;
              return (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps({ title: undefined }))}
                  style={style}
                  onClick={(e) => renderSortingIcon(e, column)}
                >
                  <div className={`header-column ${column.headerClassName} ?? ''`}>
                    {column.render('Header')}
                    {/* Add a sort direction indicator */}
                    {column.id !== '_selector' && column.sortAccessor !== undefined && (
                      <SortingIndicator
                        direction={
                          column.sortAccessor === props.sort.accessor
                            ? props.sort.sortDirection
                            : SortDirection.None
                        }
                        column={column}
                        onClick={onSortingIconClick}
                        isVisible={
                          props.visibleSortIndicator ||
                          column.sortAccessor === props.currentSortedColumn
                        }
                      />
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        ))}
      </TableHeader>
      <TableBody {...getTableBodyProps()} tableClassName={props.tableClassName}>
        {rows.length > 0 ? (
          type == 'loyalty' || type == 'preOwnedRates' ? (
            rows.map((row) => {
              if (row.original.visible) {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps({
                      onClick: () => props.onRowClick && props.onRowClick(row),
                    })}
                  >
                    {row.cells.map((cell) => {
                      return (
                        <td {...cell.getCellProps()} className={cellClassName(cell)}>
                          {cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>
                );
              }
            })
          ) : (
            rows.map((row) => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps({ onClick: () => props.onRowClick && props.onRowClick(row) })}
                >
                  {row.cells.map((cell) => {
                    return (
                      <td
                        {...cell.getCellProps({
                          onClick: () => cell.column.onCellClick && cell.column.onCellClick(cell),
                        })}
                        className={cellClassName(cell)}
                      >
                        {cell.render('Cell')}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )
        ) : (
          <tr>
            <td
              colSpan={headerGroups[0].headers.length}
              className={props.noResultFoundBody ? 'no-result' : ''}
            >
              {props.noResultFoundBody ?? <NoRecordFound>No results found</NoRecordFound>}
            </td>
          </tr>
        )}
      </TableBody>
    </table>
  );
};

export default ReactTableWrapper;

const NoRecordFound = styled.div`
  font-size: ${(props) => props.theme.typography.h4}px;
  font-weight: ${(props) => props.theme.typography.fontWeights.bold};
  color: ${(props) => props.theme.palette.darkGray};
  display: inline-block;
  text-align: center;
  padding-top: 0px;
  width: 100%;
`;

type TableWrapperProps = {
  canSelectRows?: boolean;
  backgroundColor?: string;
  color?: string;
};

const TableWrapper = styled.table.attrs<TableWrapperProps>({
  className: `react-table-wrapper ${(props) => (props.canSelectRows ? 'enabled-checkbox' : '')}`,
})<TableWrapperProps>`
  thead {
    background-color: ${(props) => props.backgroundColor ?? props.theme.palette.grayTwo};
    color: ${(props) => props.color ?? props.theme.palette.gray};
  }
`;
